import { useMemo, useState } from 'react'
import {
  addDays,
  compareIsoDate,
  daysInRange,
  endOfMonthGrid,
  eventColorVar,
  fromIsoDate,
  startOfMonthGrid,
  toIsoDate,
  type CalendarEvent,
} from '../calendarHelpers'
import { Pressable } from '../../Common/Pressable'

interface MonthViewProps {
  focusedDate: Date
  events: CalendarEvent[]
  onEventClick: (event: CalendarEvent) => void
  onDayNumberClick: (date: Date) => void
  onCellClick: (date: Date) => void
  /** Fired when the user drags across two or more cells (start !== end). */
  onRangeSelect?: (startIso: string, endIso: string) => void
  onEventDragStart: (event: CalendarEvent, dateIso: string) => void
  onEventDragOver: (dateIso: string) => void
  onEventDragEnd: () => void
  overrideStartIso?: string
  overrideEndIso?: string
  draggingEventId?: string | null
}

const overlapsRange = (event: CalendarEvent, rowStart: string, rowEnd: string) =>
  compareIsoDate(event.endDate, rowStart) >= 0 && compareIsoDate(event.startDate, rowEnd) <= 0

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const todayIso = () => toIsoDate(new Date())

export const MonthView = ({
  focusedDate,
  events,
  onEventClick,
  onDayNumberClick,
  onCellClick,
  onRangeSelect,
  onEventDragStart,
  onEventDragOver,
  onEventDragEnd,
  overrideStartIso,
  overrideEndIso,
  draggingEventId,
}: MonthViewProps) => {
  const [rangeAnchorIso, setRangeAnchorIso] = useState<string | null>(null)
  const [rangeHoverIso, setRangeHoverIso] = useState<string | null>(null)

  const rangeStart = rangeAnchorIso && rangeHoverIso
    ? (rangeAnchorIso <= rangeHoverIso ? rangeAnchorIso : rangeHoverIso)
    : null
  const rangeEnd = rangeAnchorIso && rangeHoverIso
    ? (rangeAnchorIso <= rangeHoverIso ? rangeHoverIso : rangeAnchorIso)
    : null

  const finishRangeSelection = () => {
    if (rangeAnchorIso && rangeHoverIso && rangeAnchorIso !== rangeHoverIso && onRangeSelect) {
      const start = rangeAnchorIso <= rangeHoverIso ? rangeAnchorIso : rangeHoverIso
      const end = rangeAnchorIso <= rangeHoverIso ? rangeHoverIso : rangeAnchorIso
      onRangeSelect(start, end)
    }
    setRangeAnchorIso(null)
    setRangeHoverIso(null)
  }
  const weeks = useMemo(() => {
    const gridStart = startOfMonthGrid(focusedDate)
    const gridEnd = endOfMonthGrid(focusedDate)
    const allDays = daysInRange(gridStart, gridEnd)
    const result: Date[][] = []
    for (let i = 0; i < allDays.length; i += 7) result.push(allDays.slice(i, i + 7))
    return result
  }, [focusedDate])

  const today = todayIso()

  return (
    <div
      className="calMonth"
      data-cal-view="month"
      onPointerUp={() => {
        onEventDragEnd()
        finishRangeSelection()
      }}
      onPointerLeave={() => {
        // Cancel any in-flight range selection if the pointer leaves the grid.
        if (rangeAnchorIso) {
          setRangeAnchorIso(null)
          setRangeHoverIso(null)
        }
      }}
    >
      <div className="calMonthHead">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="calMonthHeadCell">{label}</div>
        ))}
      </div>
      <div className="calMonthBody">
        {weeks.map((week, weekIdx) => {
          const rowStart = toIsoDate(week[0])
          const rowEnd = toIsoDate(week[6])
          const rowEvents = events.filter((event) => overlapsRange(event, rowStart, rowEnd))
          // Lay multi-day bars across the week with greedy slotting.
          const lanes: CalendarEvent[][] = []
          for (const event of rowEvents) {
            let placed = false
            for (const lane of lanes) {
              if (!lane.some((other) => overlapsRange(other, event.startDate, event.endDate))) {
                lane.push(event)
                placed = true
                break
              }
            }
            if (!placed) lanes.push([event])
          }
          // Row min-height grows with the number of event lanes so bars never
          // overlap day numbers or each other. Reserve space for: top day-number
          // strip (28px on most styles, ~36 on Aurora), bottom padding (~10px),
          // and per-lane height (22 bar + 3 gap ≈ 25px).
          const LANE_HEIGHT = 26
          const RESERVED_TOP = 38
          const RESERVED_BOTTOM = 10
          const baseMinHeight = 110
          const rowMinHeight = Math.max(
            baseMinHeight,
            RESERVED_TOP + RESERVED_BOTTOM + lanes.length * LANE_HEIGHT,
          )
          return (
            <div
              key={weekIdx}
              className="calMonthRow"
              style={{ minHeight: `${rowMinHeight}px` }}
            >
              <div className="calMonthRowGrid">
                {week.map((day) => {
                  const iso = toIsoDate(day)
                  const isOtherMonth = day.getMonth() !== focusedDate.getMonth()
                  const isToday = iso === today
                  const isInRange = rangeStart && rangeEnd && iso >= rangeStart && iso <= rangeEnd
                  return (
                    <div
                      key={iso}
                      className={`calMonthCell${isOtherMonth ? ' isMuted' : ''}${isToday ? ' isToday' : ''}${isInRange ? ' isRangeSelecting' : ''}`}
                      onPointerDown={(event) => {
                        const target = event.target as HTMLElement
                        if (target.closest('.calMonthDay')) return
                        if (target.closest('.calMonthBar')) return
                        if (event.button !== 0) return
                        if (!onRangeSelect) return
                        setRangeAnchorIso(iso)
                        setRangeHoverIso(iso)
                      }}
                      onPointerEnter={() => {
                        if (draggingEventId) {
                          onEventDragOver(iso)
                          return
                        }
                        if (rangeAnchorIso) setRangeHoverIso(iso)
                      }}
                      onClick={(event) => {
                        if ((event.target as HTMLElement).closest('.calMonthDay')) return
                        if ((event.target as HTMLElement).closest('.calMonthBar')) return
                        // Skip the single-day click when a drag range selection
                        // just landed — the pointer-up handler already fired
                        // onRangeSelect.
                        if (rangeAnchorIso && rangeHoverIso && rangeAnchorIso !== rangeHoverIso) return
                        onCellClick(day)
                      }}
                    >
                      <Pressable
                        className="calMonthDay"
                        onClick={(event) => {
                          event.stopPropagation()
                          onDayNumberClick(day)
                        }}
                      >
                        {day.getDate()}
                      </Pressable>
                    </div>
                  )
                })}
              </div>
              <div className="calMonthBars">
                {lanes.map((lane, laneIdx) =>
                  lane.map((event) => {
                    const clippedStart = compareIsoDate(event.startDate, rowStart) >= 0 ? event.startDate : rowStart
                    const clippedEnd = compareIsoDate(event.endDate, rowEnd) <= 0 ? event.endDate : rowEnd
                    const startIdx = daysFromRowStart(week, clippedStart)
                    const endIdx = daysFromRowStart(week, clippedEnd)
                    const isDragging = event.id === draggingEventId
                    const overridden =
                      isDragging && overrideStartIso && overrideEndIso
                        ? `${overrideStartIso} → ${overrideEndIso}`
                        : null
                    return (
                      <div
                        key={`${event.id}-${weekIdx}`}
                        className={`calMonthBar calMonthBar--${event.kind}${isDragging ? ' isDragging' : ''}${event.isCancelled ? ' isCancelled' : ''}`}
                        style={{
                          gridColumnStart: startIdx + 1,
                          gridColumnEnd: endIdx + 2,
                          gridRowStart: laneIdx + 1,
                          ['--cal-accent' as string]: eventColorVar(event.kind),
                        } as React.CSSProperties}
                        onClick={() => onEventClick(event)}
                        onPointerDown={(pointerEvent) => {
                          pointerEvent.stopPropagation()
                          onEventDragStart(event, clippedStart)
                        }}
                        title={`${event.title} (${event.startDate}${event.endDate !== event.startDate ? ' → ' + event.endDate : ''})`}
                      >
                        <span className="calMonthBarLabel">
                          {event.title}
                        </span>
                        {overridden && <span className="calMonthBarOverride">{overridden}</span>}
                      </div>
                    )
                  }),
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const daysFromRowStart = (week: Date[], iso: string) => {
  const target = fromIsoDate(iso).getTime()
  let idx = 0
  for (let i = 0; i < week.length; i++) {
    if (week[i].getTime() <= target) idx = i
  }
  // Snap so events outside the row clip to the boundary correctly.
  const first = week[0].getTime()
  const last = addDays(week[6], 0).getTime()
  if (target < first) return 0
  if (target > last) return 6
  return idx
}
